import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { apiService, APIError, refreshAccessToken } from '@/services/api'
import { resetAdminNotificationStreamCursor } from '@/utils/adminNotificationCursor'
import { resetSharedNotificationStreamState } from '@/composables/useAdminNotificationStream'
import { STORAGE_KEYS } from '@/utils/storageKeys'
import {
  clearAuthTokens,
  getAccessToken,
  getRefreshToken,
  restoreAdminRefreshToken,
  setAuthTokens,
  shouldAttemptSilentRefresh,
  stashAdminSessionForImpersonate,
  usesHttpOnlyRefresh,
} from '@/utils/authTokens'
import { resetSessionActivity, setSessionMeta, clearSessionMeta } from '@/utils/sessionConfig'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref(null)
  const token = ref(getAccessToken())
  const loading = ref(false)
  let checkAuthPromise = null
  const _impersonating = ref(!!localStorage.getItem(STORAGE_KEYS.ADMIN_TOKEN))
  
  // Getters
  const isAuthenticated = computed(() => !!token.value && !!user.value)
  const userRole = computed(() => user.value?.role || user.value?.access || 'guest')
  const isSuperAdmin = computed(() => userRole.value === 'super_admin')
  const isAdmin = computed(() => ['admin', 'super_admin'].includes(userRole.value))
  const isReseller = computed(() => ['reseller', 'sub_reseller', 'revendedor'].includes(userRole.value))
  const isImpersonating = computed(() => _impersonating.value)
  const canImpersonateSubReseller = computed(() => {
    if (isImpersonating.value) return false
    return isSuperAdmin.value || isReseller.value
  })
  
  const hasPermission = computed(() => (permission) => {
    // Super admin tem todas as permissões
    if (isSuperAdmin.value) {
      return true
    }
    
    const permissions = user.value?.permissions || []
    
    // Wildcard
    if (permissions.includes('*')) {
      return true
    }
    
    // Permissão específica
    if (permissions.includes(permission)) {
      return true
    }
    
    // Permissão de categoria (ex: 'categories.*' permite 'categories.create')
    const categoryPermission = permission.split('.')[0] + '.*'
    return permissions.includes(categoryPermission)
  })
  
  // Actions
  const login = async (credentials) => {
    loading.value = true
    
    try {
      const login = String(
        credentials.login ?? credentials.email ?? credentials.username ?? ''
      ).trim()
      const response = await apiService.auth.login({
        login,
        email: login,
        username: login,
        password: credentials.password,
      })
      
      if (response.success) {
        const authData = response.data || {}
        user.value = authData.user
        token.value = authData.access_token || authData.token

        setAuthTokens({
          accessToken: token.value,
          refreshToken: authData.refresh_token,
          refreshTokenDelivery: authData.refresh_token_delivery,
          clearRefreshIfMissing: true,
        })
        if (authData.session) {
          clearSessionMeta()
          setSessionMeta(authData.session)
        } else if (authData.expires_in) {
          clearSessionMeta()
          setSessionMeta({
            access_token_ttl_seconds: authData.expires_in,
          })
        }
        resetSessionActivity()
        if (authData.user) {
          localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(authData.user))
        }

        await checkAuth()
        
        return { 
          success: true, 
          user: user.value,
          redirect: response.redirect // Para redirecionar baseado no role
        }
      } else {
        console.error('❌ [AuthStore] Resposta de erro:', response)
        throw new APIError(response.message || 'Credenciais inválidas')
      }
    } catch (error) {
      console.error('❌ [AuthStore] Erro capturado:', error)
      
      // Garante que o erro seja propagado com a mensagem correta
      if (error instanceof APIError) {
        throw error
      } else if (error.response?.data?.message) {
        throw new APIError(error.response.data.message)
      } else if (error.message) {
        throw new APIError(error.message)
      } else {
        throw new APIError('Erro inesperado durante o login')
      }
    } finally {
      loading.value = false
    }
  }

  const logout = async () => {
    loading.value = true
    
    try {
      // Tenta fazer logout no servidor (não é crítico se falhar)
      try {
        await apiService.auth.logout({
          refresh_token: getRefreshToken() || undefined,
        })
      } catch (error) {
        console.warn('Erro ao fazer logout no servidor:', error)
      }
    } finally {
      // Limpa dados locais mesmo se o logout no servidor falhar
      clearAuthData()
      loading.value = false
    }
  }

  const clearAuthData = () => {
    user.value = null
    token.value = null
    impersonatedBy.value = null
    _impersonating.value = false
    clearAuthTokens()
    resetAdminNotificationStreamCursor()
    resetSharedNotificationStreamState()
  }

  const checkAuth = async () => {
    if (checkAuthPromise) {
      return checkAuthPromise
    }

    checkAuthPromise = (async () => {
    if (!token.value) {
      const tokenFromStorage = getAccessToken()
      if (tokenFromStorage) {
        token.value = tokenFromStorage
      } else if (getRefreshToken()) {
        const refreshed = await refreshToken()
        if (!refreshed) {
          clearAuthData()
          return false
        }
      } else {
        clearAuthData()
        return false
      }
    }
    
    const cachedUser = localStorage.getItem(STORAGE_KEYS.USER)
    if (cachedUser && !user.value) {
      try {
        user.value = JSON.parse(cachedUser)
      } catch (error) {
        console.error('❌ [AuthStore] Erro ao parsear dados do usuário:', error)
        clearAuthData()
        return false
      }
    }
    
    loading.value = true
    
    try {
      const response = await apiService.auth.me()
      
      if (response.success) {
        user.value = response.data.user
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(response.data.user))
        return true
      }

      console.error('❌ [AuthStore] Resposta sem success:', response)
      clearAuthData()
      return false
    } catch (error) {
      console.error('❌ [AuthStore] Erro ao verificar autenticação:', error)
      
      if (error.details?.auth || error.response?.status === 401) {
        if (shouldAttemptSilentRefresh()) {
          const refreshed = await refreshToken()
          if (refreshed) {
            try {
              const retry = await apiService.auth.me()
              if (retry.success) {
                user.value = retry.data.user
                localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(retry.data.user))
                return true
              }
            } catch (retryError) {
              console.error('❌ [AuthStore] Falha após refresh:', retryError)
            }
          }
        }
        clearAuthData()
      }
      
      return false
    } finally {
      loading.value = false
    }
    })()

    try {
      return await checkAuthPromise
    } finally {
      checkAuthPromise = null
    }
  }

  const refreshToken = async () => {
    if (!usesHttpOnlyRefresh() && !getRefreshToken()) return false

    try {
      const newAccessToken = await refreshAccessToken()
      if (newAccessToken) {
        token.value = newAccessToken
        return true
      }
      return false
    } catch (error) {
      console.error('Erro ao renovar token:', error)
      const status = error?.response?.status
      if (status === 401 || status === 403) {
        clearAuthData()
      }
      return false
    }
  }

  const updateUser = (userData) => {
    if (user.value) {
      user.value = { ...user.value, ...userData }
      localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(user.value))
    }
  }

  // Impersonate - Admin/revenda entra na conta de sub-revendedor
  const impersonatedBy = ref(null)

  const impersonate = async (userId) => {
    try {
      loading.value = true
      
      // Salva token e dados do admin atual ANTES de trocar
      const currentToken = token.value
      const currentUser = JSON.parse(JSON.stringify(user.value))
      
      const response = await apiService.auth.impersonate(userId)
      
      if (response.success) {
        // Guarda token admin original no localStorage (chave separada)
        localStorage.setItem(STORAGE_KEYS.ADMIN_TOKEN, currentToken)
        localStorage.setItem(STORAGE_KEYS.ADMIN_USER, JSON.stringify(currentUser))
        stashAdminSessionForImpersonate()
        _impersonating.value = true
        
        // Troca para o token do revendedor
        token.value = response.data.token
        user.value = response.data.user
        impersonatedBy.value = response.data.impersonated_by
        
        setAuthTokens({ accessToken: response.data.token })
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(response.data.user))
        
        return { success: true, user: response.data.user }
      } else {
        throw new Error(response.message || 'Erro ao impersonar')
      }
    } catch (error) {
      console.error('Erro ao impersonar:', error)
      throw error
    } finally {
      loading.value = false
    }
  }

  const stopImpersonate = async () => {
    try {
      loading.value = true
      
      const adminToken = localStorage.getItem(STORAGE_KEYS.ADMIN_TOKEN)
      
      if (!adminToken) {
        throw new Error('Token do admin não encontrado')
      }
      
      const response = await apiService.auth.stopImpersonate(adminToken)
      
      if (response.success) {
        // Restaura token do admin
        token.value = response.data.token
        user.value = response.data.user
        impersonatedBy.value = null
        
        setAuthTokens({ accessToken: response.data.token })
        localStorage.setItem(STORAGE_KEYS.USER, JSON.stringify(response.data.user))
        
        // Limpa dados de impersonate
        localStorage.removeItem(STORAGE_KEYS.ADMIN_TOKEN)
        localStorage.removeItem(STORAGE_KEYS.ADMIN_USER)
        restoreAdminRefreshToken()
        _impersonating.value = false
        
        return { success: true }
      } else {
        throw new Error(response.message || 'Erro ao voltar')
      }
    } catch (error) {
      console.error('Erro ao parar impersonate:', error)
      // Fallback: restaura do localStorage
      const adminToken = localStorage.getItem(STORAGE_KEYS.ADMIN_TOKEN)
      const adminUser = localStorage.getItem(STORAGE_KEYS.ADMIN_USER)
      if (adminToken && adminUser) {
        token.value = adminToken
        try {
          user.value = JSON.parse(adminUser)
        } catch {
          user.value = null
        }
        setAuthTokens({ accessToken: adminToken })
        localStorage.setItem(STORAGE_KEYS.USER, adminUser)
        localStorage.removeItem(STORAGE_KEYS.ADMIN_TOKEN)
        localStorage.removeItem(STORAGE_KEYS.ADMIN_USER)
        restoreAdminRefreshToken()
        impersonatedBy.value = null
        _impersonating.value = false
      }
      throw error
    } finally {
      loading.value = false
    }
  }

  const initialize = async () => {
    const access = getAccessToken()
    const refresh = getRefreshToken()

    if (access) {
      token.value = access
    }

    if (!access && refresh) {
      await refreshToken()
    }

    if (token.value || getRefreshToken()) {
      await checkAuth()
    }
  }

  // Auto-inicializar quando o store é criado
  initialize()

  return {
    // State
    user,
    token,
    loading,
    
    // Getters
    isAuthenticated,
    userRole,
    isSuperAdmin,
    isAdmin,
    isReseller,
    canImpersonateSubReseller,
    hasPermission,
    isImpersonating,
    impersonatedBy,
    
    // Actions
    login,
    logout,
    checkAuth,
    refreshToken,
    updateUser,
    initialize,
    clearAuthData,
    impersonate,
    stopImpersonate
  }
})
